import { useEffect } from 'react';
import { fetchQuery, useRelayEnvironment, graphql } from 'react-relay';
import { fg } from '@atlassian/jira-feature-gating';
import { createLocalStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/local-storage/index.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type {
	useCreateProjectCheckPermissionsQuery,
	useCreateProjectCheckPermissionsQuery$data,
} from '@atlassian/jira-relay/src/__generated__/useCreateProjectCheckPermissionsQuery.graphql';

const localStorageProvider = createLocalStorageProvider('analytics-permission-check');

/**
 * Created to fire an analytics event once, with the user permission for creating team managed projects.
 * Experiment preparation, gathering information
 * Team Groot https://atlassian.enterprise.slack.com/archives/CFJ89SYT0
 * @see https://atl-growth.atlassian.net/browse/GROOT-2002
 */
export const useCreateProjectCheckPermissions = (): void => {
	const environment = useRelayEnvironment();
	const cloudId = useCloudId();
	const isAdmin = useIsAdmin();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(() => {
		if (!localStorageProvider.get('complete') && fg('groot-create-team-project-permission-check')) {
			fetchQuery<useCreateProjectCheckPermissionsQuery>(
				environment,
				graphql`
					query useCreateProjectCheckPermissionsQuery($cloudId: ID!) {
						jira {
							canCreateProject: canPerform(type: CREATE_PROJECT, cloudId: $cloudId)
								@optIn(to: "JiraAction")
							canCreateTeamManagedProject: canPerform(
								type: CREATE_TEAM_MANAGED_PROJECT
								cloudId: $cloudId
							) @optIn(to: "JiraAction")
							canCreateCompanyManagedProject: canPerform(
								type: CREATE_COMPANY_MANAGED_PROJECT
								cloudId: $cloudId
							) @optIn(to: "JiraAction")
						}
					}
				`,
				{ cloudId },
			).subscribe({
				next: (data: useCreateProjectCheckPermissionsQuery$data) => {
					fireTrackAnalytics(createAnalyticsEvent({}), 'createProject userPermissions', {
						canCreateProject: !!data?.jira?.canCreateProject,
						canCreateTeamManagedProject: !!data?.jira?.canCreateTeamManagedProject,
						canCreateCompanyManagedProject: !!data?.jira?.canCreateCompanyManagedProject,
						isAdmin,
					});
				},
			});
			localStorageProvider.set('complete', true);
		}
	});
};
